import classNames from "classnames";
import { number } from "prop-types";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useOutsideAlerter from "../hooks/use_outside_alerter";

export function useWindowSize() {
	// Initialize state with undefined width/height so server and client renders match
	// Learn more here: https://joshwcomeau.com/react/the-perils-of-rehydration/
	const [windowSize, setWindowSize] = useState({
		width: undefined as number,
		height: undefined as number,
	});
	useEffect(() => {
		// Handler to call on window resize
		function handleResize() {
			// Set window width/height to state
			setWindowSize({
				width: window.innerWidth,
				height: window.innerHeight,
			});
		}
		// Add event listener
		window.addEventListener("resize", handleResize);
		// Call handler right away so state gets updated with initial window size
		handleResize();
		// Remove event listener on cleanup
		return () => window.removeEventListener("resize", handleResize);
	}, []); // Empty array ensures that effect is only run on mount
	return windowSize;
}

const clamp = (num: number, min: number, max: number) =>
	Math.min(Math.max(num, min), max);

export function WithTooltipPortaled(props: {
	children: React.ReactNode;
	tooltip: JSX.Element | string;
	no_delay?: boolean;
	style: "Info" | "Error";
	disable_click?: boolean;
}) {
	const [is_active_click, setIsActiveClick] = useState(false);
	const [is_active_hover, setIsActiveHover] = useState(false);

	const windowSize = useWindowSize();

	const tooltip_ref = useRef<HTMLDivElement>(null);
	const parent_ref = useRef<HTMLDivElement>(null);
	const arrow_ref = useRef<HTMLDivElement>(null);

	useOutsideAlerter(parent_ref, () => setIsActiveClick(false));

	useEffect(() => {
		if (!is_active_click && !is_active_hover) {
			return;
		}

		if (!tooltip_ref?.current || !parent_ref || !windowSize || !arrow_ref) {
			return;
		}

		const { width: t_width, height: t_height } =
			tooltip_ref.current.getBoundingClientRect();

		const {
			left: p_left,
			width: p_width,
			top: p_top,
		} = parent_ref.current.getBoundingClientRect();

		// Hack to determine border length, since can't set child relative to border
		const border_offset = getComputedStyle(
			tooltip_ref.current
		).borderLeftWidth;

		const border_thickness = Number.parseInt(
			border_offset.substring(0, border_offset.length - 2)
		);

		const { width: a_width } = arrow_ref.current.getBoundingClientRect();

		const desired_absolute_tooltip_x = p_left + p_width / 2 - t_width / 2;

		const absolute_tooltip_x = clamp(
			desired_absolute_tooltip_x,
			0,
			windowSize.width - t_width
		);

		tooltip_ref.current.style.left = `${absolute_tooltip_x}px`;
		tooltip_ref.current.style.top = `${p_top - t_height}px`;

		const desired_absolute_arrow_x = p_left + p_width / 2 - a_width / 2;

		arrow_ref.current.style.left = `${(t_width - a_width) / 2}px`;

		// it is one pixel off for some reason
	}, [
		tooltip_ref,
		parent_ref,
		arrow_ref,
		windowSize,
		is_active_click,
		is_active_hover,
	]);

	return (
		<div
			className={"dropdown inline-flex w-fit max-w-full items-center"}
			ref={parent_ref}
			onMouseLeave={() => setIsActiveHover(false)}
		>
			<div className="dropdown-trigger max-w-full flex-grow">
				<div
					className="max-w-full flex-grow cursor-pointer"
					aria-haspopup="true"
					aria-controls="dropdown-menu4"
					onClick={
						props.disable_click
							? undefined
							: (e) => {
									e.preventDefault();
									setIsActiveClick(true);
							  }
					}
					onMouseEnter={() => setIsActiveHover(true)}
				>
					{props.children}
				</div>
			</div>

			{props.tooltip && (
				<>
					{createPortal(
						<div
							className={classNames(
								"has-text-centered absolute z-[1003] mb-2 flex h-max w-max max-w-[15rem] overflow-auto rounded-sm p-2 text-xs font-semibold drop-shadow-xl transition-opacity sm:max-h-[24rem] sm:max-w-[24rem] sm:!p-3 sm:text-sm",
								{
									"pointer-events-none": !is_active_click,
									"tooltip-v2": props.style === "Info",
									"tooltip-v2--error":
										props.style === "Error",
									"delay-500":
										is_active_hover &&
										!is_active_click &&
										!props.no_delay,
									"delay-0":
										is_active_hover &&
										!is_active_click &&
										props.no_delay,
									"transition-none": is_active_click,
									"invisible opacity-0":
										!is_active_click && !is_active_hover,
									"visible opacity-[97%]":
										is_active_click || is_active_hover,
								}
							)}
							id="dropdown-menu4"
							role="menu"
							ref={tooltip_ref}
						>
							<span className="overflow-auto">
								{props.tooltip}
							</span>
							<div
								className="tooltip-arrow"
								ref={arrow_ref}
							></div>
						</div>,
						document.body
					)}
				</>
			)}
		</div>
	);
}
